<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 条件搜索 -->
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="6" :sm="24">
              <a-form-item label="采购单号">
                <a-input v-model="queryParam.initNo" placeholder="请输入采购单号" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-input v-model="queryParam.supplierName" placeholder="请输入供应商名称" allow-clear />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="状态">
                <a-select v-model="queryParam.status" placeholder="请选择状态" allow-clear style="width: 100%">
                  <a-select-option :value="1">待审核</a-select-option>
                  <a-select-option :value="2">已审核</a-select-option>
                  <a-select-option :value="3">已取消</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <span class="table-page-search-submitButtons">
                <a-button type="primary" @click="handleQuery">
                  <a-icon type="search" />查询
                </a-button>
                <a-button style="margin-left: 8px" @click="resetQuery">
                  <a-icon type="redo" />重置
                </a-button>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>

      <!-- 操作 -->
      <div class="table-operations">
        <a-button type="primary" @click="handleAdd" v-hasPermi="['retail:inventory:init']">
          <a-icon type="plus" />采购入库
        </a-button>
      </div>

      <!-- 采购入库表单 -->
      <init-form ref="initForm" @ok="getList" />

      <!-- 记录列表 -->
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="id"
        :columns="columns"
        :data-source="list"
        :pagination="pagination"
        :bordered="tableBordered"
        @change="handleTableChange"
      >
        <span slot="status" slot-scope="text">
          <a-tag :color="getStatusColor(text)">{{ getStatusText(text) }}</a-tag>
        </span>
        <span slot="totalAmount" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
        <span slot="createTime" slot-scope="text">
          {{ parseTime(text) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a @click="handleDetail(record)" style="margin-right: 8px">
            <a-icon type="eye" />查看详情
          </a>
          <a v-if="record.status === 1" @click="handleAudit(record)" style="margin-right: 8px" v-hasPermi="['retail:inventory:audit']">
            <a-icon type="check" />审核通过
          </a>
          <a v-if="record.status === 1" @click="handleReject(record)" v-hasPermi="['retail:inventory:audit']">
            <a-icon type="close" />审核拒绝
          </a>
        </span>
      </a-table>
    </a-card>

    <!-- 明细弹窗 -->
    <a-modal
      :title="`采购明细 - ${currentRecord?.initNo || ''}`"
      :visible="detailVisible"
      :footer="null"
      @cancel="detailVisible = false"
      width="1000px"
      :centered="true"
    >
      <a-descriptions bordered :column="2" style="margin-bottom: 16px">
        <a-descriptions-item label="采购单号">{{ currentRecord?.initNo }}</a-descriptions-item>
        <a-descriptions-item label="供应商">{{ currentRecord?.supplierName || '-' }}</a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="getStatusColor(currentRecord?.status)">{{ getStatusText(currentRecord?.status) }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="审核人">{{ currentRecord?.auditor || '-' }}</a-descriptions-item>
        <a-descriptions-item label="商品数量">{{ currentRecord?.totalGoods }}</a-descriptions-item>
        <a-descriptions-item label="采购总量">{{ currentRecord?.totalStock }}</a-descriptions-item>
        <a-descriptions-item label="总金额">{{ currentRecord?.totalAmount ? '¥' + parseFloat(currentRecord.totalAmount).toFixed(2) : '¥0.00' }}</a-descriptions-item>
        <a-descriptions-item label="备注" :span="2">{{ currentRecord?.remark || '-' }}</a-descriptions-item>
        <a-descriptions-item v-if="currentRecord?.auditRemark" label="审核备注" :span="2">{{ currentRecord?.auditRemark }}</a-descriptions-item>
        <a-descriptions-item label="创建时间">{{ parseTime(currentRecord?.createTime) }}</a-descriptions-item>
        <a-descriptions-item label="审核时间">{{ parseTime(currentRecord?.auditTime) }}</a-descriptions-item>
      </a-descriptions>

      <a-table
        :loading="detailLoading"
        rowKey="id"
        :columns="detailColumns"
        :data-source="detailList"
        :pagination="false"
        size="small"
      >
        <span slot="goodsName" slot-scope="text, record">
          {{ text }} <a-tag v-if="record.skuName" color="orange">{{ record.skuName }}</a-tag>
        </span>
        <span slot="stock" slot-scope="text">
          <a-tag color="blue">{{ text }}</a-tag>
        </span>
        <span slot="purchasePrice" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
        <span slot="merchantPrice" slot-scope="text">
          ¥{{ text ? parseFloat(text).toFixed(2) : '0.00' }}
        </span>
      </a-table>
    </a-modal>

    <!-- 审核弹窗 -->
    <a-modal
      title="审核采购单"
      :visible="auditVisible"
      @ok="handleAuditConfirm"
      @cancel="auditVisible = false"
      :confirm-loading="auditLoading"
      width="800px"
      :centered="true"
    >
      <a-descriptions bordered :column="2" style="margin-bottom: 16px">
        <a-descriptions-item label="采购单号">{{ auditRecord?.initNo }}</a-descriptions-item>
        <a-descriptions-item label="供应商">{{ auditRecord?.supplierName || '-' }}</a-descriptions-item>
      </a-descriptions>

      <a-alert message="请确认实际入库数量，审核通过后将按此数量增加库存" type="info" show-icon style="margin-bottom: 16px" />

      <a-table
        :columns="auditDetailColumns"
        :data-source="auditDetailList"
        :pagination="false"
        size="small"
        rowKey="id"
      >
        <span slot="goodsName" slot-scope="text, record">
          {{ text }} <a-tag v-if="record.skuName" color="orange">{{ record.skuName }}</a-tag>
        </span>
        <template slot="stock" slot-scope="text, record">
          <a-tag color="blue">{{ text }}</a-tag>
        </template>
        <template slot="actualStock" slot-scope="text, record, index">
          <a-input-number
            v-model="record.actualStock"
            :min="0"
            :max="99999"
            :precision="0"
            style="width: 100%"
            placeholder="请输入实际入库数量"
          />
        </template>
      </a-table>
    </a-modal>
  </page-header-wrapper>
</template>

<script>
import { getInitList, getInitItemList, auditInitRecord, rejectInitRecord } from '@/api/retail/inventory'
import InitForm from './modules/InitForm'
import { tableMixin } from '@/store/table-mixin'
import moment from 'moment'

export default {
  name: 'PurchaseRecord',
  components: {
    InitForm
  },
  mixins: [tableMixin],
  data() {
    return {
      list: [],
      loading: false,
      detailVisible: false,
      detailLoading: false,
      detailList: [],
      currentRecord: null,
      auditVisible: false,
      auditLoading: false,
      auditRecord: null,
      auditDetailList: [],
      auditRemark: '',
      queryParam: {
        initNo: undefined,
        supplierName: undefined,
        status: undefined
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `共 ${total} 条`,
        pageSizeOptions: ['10', '20', '30', '50', '100']
      },
      columns: [
        {
          title: '采购单号',
          dataIndex: 'initNo',
          width: 180,
          scopedSlots: { customRender: 'initNo' },
          align: 'center'
        },
        {
          title: '供应商',
          dataIndex: 'supplierName',
          width: 200,
          align: 'center'
        },
        {
          title: '商品数量',
          dataIndex: 'totalGoods',
          width: 100,
          align: 'center'
        },
        {
          title: '采购总量',
          dataIndex: 'totalStock',
          width: 100,
          align: 'center'
        },
        {
          title: '实际入库总量',
          dataIndex: 'totalActualStock',
          width: 120,
          align: 'center'
        },
        {
          title: '总金额',
          dataIndex: 'totalAmount',
          width: 120,
          scopedSlots: { customRender: 'totalAmount' },
          align: 'center'
        },
        {
          title: '状态',
          dataIndex: 'status',
          width: 100,
          scopedSlots: { customRender: 'status' },
          align: 'center'
        },
        {
          title: '创建时间',
          dataIndex: 'createTime',
          width: 180,
          scopedSlots: { customRender: 'createTime' },
          align: 'center'
        },
        {
          title: '操作',
          dataIndex: 'operation',
          width: 280,
          scopedSlots: { customRender: 'operation' },
          align: 'center',
          fixed: 'right'
        }
      ],
      detailColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          width: 300,
          ellipsis: true,
          scopedSlots: { customRender: 'goodsName' },
          align: 'center'
        },
        {
          title: '采购数量',
          dataIndex: 'stock',
          key: 'stock',
          width: 100,
          scopedSlots: { customRender: 'stock' },
          align: 'center'
        },
        {
          title: '采购价(元)',
          dataIndex: 'purchasePrice',
          key: 'purchasePrice',
          width: 120,
          scopedSlots: { customRender: 'purchasePrice' },
          align: 'center'
        },
        {
          title: '商家拿货价(元)',
          dataIndex: 'merchantPrice',
          key: 'merchantPrice',
          width: 130,
          scopedSlots: { customRender: 'merchantPrice' },
          align: 'center'
        },
        {
          title: '实际入库数量',
          dataIndex: 'actualStock',
          key: 'actualStock',
          width: 120,
          align: 'center'
        },
        {
          title: '预警库存',
          dataIndex: 'alarmStock',
          key: 'alarmStock',
          width: 100,
          align: 'center'
        }
      ],
      auditDetailColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          width: 300,
          ellipsis: true,
          scopedSlots: { customRender: 'goodsName' },
          align: 'center'
        },
        {
          title: '采购数量',
          dataIndex: 'stock',
          key: 'stock',
          width: 120,
          scopedSlots: { customRender: 'stock' },
          align: 'center'
        },
        {
          title: '实际入库数量',
          dataIndex: 'actualStock',
          key: 'actualStock',
          width: 150,
          scopedSlots: { customRender: 'actualStock' },
          align: 'center'
        }
      ]
    }
  },
  created() {
    this.getList()
  },
  methods: {
    moment,
    parseTime(time) {
      return time ? moment(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    getList() {
      this.loading = true
      const params = {
        ...this.queryParam,
        current: this.pagination.current,
        size: this.pagination.pageSize
      }
      getInitList(params).then(response => {
        if (response.data) {
          this.list = response.data.records || []
          this.pagination.total = response.data.total || 0
        } else if (Array.isArray(response)) {
          this.list = response
        } else {
          this.list = []
          this.pagination.total = 0
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    handleQuery() {
      this.pagination.current = 1
      this.getList()
    },
    resetQuery() {
      this.queryParam = {
        initNo: undefined,
        supplierName: undefined,
        status: undefined
      }
      this.pagination.current = 1
      this.getList()
    },
    handleTableChange(pagination) {
      this.pagination = pagination
      this.getList()
    },
    handleAdd() {
      this.$refs.initForm.handleAdd()
    },
    handleDetail(record) {
      this.currentRecord = record
      this.detailVisible = true
      this.detailLoading = true
      getInitItemList({
        initId: record.id
      }).then(response => {
        if (response.data) {
          this.detailList = Array.isArray(response.data) ? response.data : []
        } else if (Array.isArray(response)) {
          this.detailList = response
        } else {
          this.detailList = []
        }
        this.detailLoading = false
      }).catch(() => {
        this.detailLoading = false
      })
    },
    handleAudit(record) {
      this.auditRecord = record
      this.auditVisible = true
      this.auditLoading = true
      getInitItemList({
        initId: record.id
      }).then(response => {
        if (response.data) {
          this.auditDetailList = Array.isArray(response.data) ? response.data.map(item => ({
            ...item,
            actualStock: item.stock
          })) : []
        } else if (Array.isArray(response)) {
          this.auditDetailList = response.map(item => ({
            ...item,
            actualStock: item.stock
          }))
        } else {
          this.auditDetailList = []
        }
        this.auditLoading = false
      }).catch(() => {
        this.auditLoading = false
      })
    },
    handleAuditConfirm() {
      const invalidItem = this.auditDetailList.find(item => item.actualStock === undefined || item.actualStock === null || item.actualStock === '')
      if (invalidItem) {
        this.$message.warning('请为所有商品填写实际入库数量')
        return
      }

      const params = {
        initId: this.auditRecord.id,
        items: this.auditDetailList.map(item => ({
          id: item.id,
          actualStock: item.actualStock
        }))
      }

      this.auditLoading = true
      auditInitRecord(params).then(() => {
        this.$message.success('审核通过成功')
        this.auditVisible = false
        this.getList()
        this.auditLoading = false
      }).catch(error => {
        this.auditLoading = false
        this.$message.error('审核失败：' + (error.message || '未知错误'))
      })
    },
    handleReject(record) {
      this.$confirm({
        title: '确认审核拒绝',
        content: h => (
          <div>
            <p>确定要拒绝采购单【{record.initNo}】吗?</p>
            <a-input
              v-model={this.auditRemark}
              placeholder="请输入拒绝原因（选填）"
              style={{ marginTop: '10px' }}
              maxlength="200"
              allowClear
            />
          </div>
        ),
        onOk: () => {
          rejectInitRecord({ initId: record.id, auditRemark: this.auditRemark }).then(() => {
            this.$message.success('已拒绝该采购申请')
            this.auditRemark = ''
            this.getList()
          }).catch(error => {
            this.$message.error('操作失败：' + (error.message || '未知错误'))
          })
        },
        onCancel: () => {
          this.auditRemark = ''
        }
      })
    },
    getStatusText(status) {
      const statusMap = {
        1: '待审核',
        2: '已审核',
        3: '已取消'
      }
      return statusMap[status] || '未知'
    },
    getStatusColor(status) {
      const colorMap = {
        1: 'orange',
        2: 'green',
        3: 'red'
      }
      return colorMap[status] || 'default'
    }
  }
}
</script>
